import React, { memo, useMemo } from 'react'
import { POSITION_TYPE, IMapPosition, IMapStopProps, WithId } from 'transfix-maps/types'
import { Box, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { STOP_TYPE, IFullStop } from 'types/shipment'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { ILocationELD } from 'apps/fleet-planner/types'
import ShipmentMapComponent from './components/ShipmentMapComponent'

export type TrackingLocation = {
  latitude?: string
  longitude?: string
  phoneTimestamp: string
}

export interface IBaseStopLocation {
  stopType: STOP_TYPE
  relativeStopSequence: string
  address: {
    latitude?: string
    longitude?: string
  }
}

export interface IShipmentMapProps {
  recentLocations?: ILocationELD[]
  isTrackableStatus?: boolean
  pingType?: POSITION_TYPE
  /**
   * hides attraction names for high risk loads
   * @default false
   */
  hideNearbyAttractions?: boolean
}

/**
 * Maps shipment stops to a usable type for <DriverLocationsMap />
 */
const getStopLocations = (shipmentStops: IFullStop[]): IBaseStopLocation[] => {
  return shipmentStops.map(item => {
    const { facility, stopSequence: relativeStopSequence, type: stopType } = item || {}
    const { latitude, longitude } = facility || {}
    return {
      address: {
        latitude,
        longitude,
      },
      stopType,
      relativeStopSequence: String(relativeStopSequence),
    } as IBaseStopLocation
  })
}

const useShipmentMapStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: 0,
    position: 'relative',
    padding: '0 0 50%',
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${brandColors.coolGray4}`,
    '& #zoomIn, & #zoomOut': {
      paddingRight: '0 !important',
      paddingLeft: '0 !important',
      marginLeft: '0 !important',
      marginRight: '0 !important',
    },
  },
  emptyState: {
    display: 'flex',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

/*
 * Reformat recentLocations into the correct type for DriverLocationsMap component
 */

const formatTrackingLocations = (recentLocations: ILocationELD[]): TrackingLocation[] => {
  return recentLocations.map(({ latitude: lat = 0, locatedAt, longitude: lon = 0 }) => ({
    latitude: lat.toString(),
    longitude: lon.toString(),
    phoneTimestamp: locatedAt,
  }))
}

function getPositions(
  positions: TrackingLocation[],
  options?: { pingType?: POSITION_TYPE }
): WithId<IMapPosition>[] {
  const { pingType = POSITION_TYPE.LAST_PING } = options || {}
  return positions.map(({ latitude, longitude, phoneTimestamp }, index) => ({
    id: index,
    coords: {
      lat: Number(latitude),
      lon: Number(longitude),
    },
    type: pingType,
    data: {
      id: index,
      phoneTimestamp,
    },
  }))
}

const getStops = (stops: IBaseStopLocation[]): WithId<IMapStopProps>[] => {
  return stops.map((stop, id) => {
    const { address, relativeStopSequence, stopType } = stop || {}
    const { latitude, longitude } = address || {}
    return {
      coords: {
        lat: Number(latitude),
        lon: Number(longitude),
      },
      type: stopType,
      sequence: Number(relativeStopSequence),
      data: {
        id,
        ...stop,
      },
    }
  })
}

/**
 * Map that renders in the Load Details sidebar for Marketplace loads in Fleet Planner
 */
export const ShipmentMap = memo<IShipmentMapProps>(
  ({ recentLocations = [], isTrackableStatus, pingType, hideNearbyAttractions = false }) => {
    const { shipmentDetails } = useMarketplaceContext()
    const { stops: shipmentStops = [] } = shipmentDetails || {}
    /**
     * in the case where any of the following are true,
     * we will not show tracking data and only show stop information
     * TODO: FP-2840 add P44 integration
     */
    const isTrackable = recentLocations.length > 0 && isTrackableStatus

    const allStops = useMemo(() => getStopLocations(shipmentStops), [shipmentStops])
    const classes = useShipmentMapStyles()

    const trackingLocations = useMemo(
      () => (recentLocations && isTrackable ? formatTrackingLocations(recentLocations) : []),
      [isTrackable, recentLocations]
    )

    const positions = useMemo(() => getPositions(trackingLocations, { pingType }), [
      pingType,
      trackingLocations,
    ])

    const stops = useMemo(() => getStops(allStops), [allStops])

    return (
      <Box className={classes.root}>
        <Box position='absolute' top='0' bottom='0' left='0' right='0'>
          <ShipmentMapComponent
            positions={positions}
            stops={stops}
            hideNearbyAttractions={hideNearbyAttractions}
          />
        </Box>
      </Box>
    )
  }
)
